import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  MessagesSquare,
  Users,
  BookOpen,
  Settings,
  LogOut,
  Shield,
  Zap,
  Brain,
  UserCog,
  Mail,
  UserCircle,
  X,
  Building2,
  Key,
  ChevronDown,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useState, useEffect, useCallback } from 'react';
import { getApiBase } from '../../lib/utils';
import { api } from '../../lib/api';
import { useAppLanguage } from '../../i18n';

const ADVANCED_PATH_RE = /^\/(memory|end-users|workflows|email)(\/|$)/;

function readAdvancedOpenInitial(): boolean {
  try {
    const s = localStorage.getItem('sidebar_advanced_open');
    if (s !== null) return s === 'true';
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    return ADVANCED_PATH_RE.test(window.location.pathname);
  }
  return false;
}

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  badge?: string;
  badgeTone?: 'preview' | 'beta';
}

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
  onLogout?: () => void;
}

export function MobileNavDrawer({ open, onClose, onLogout }: MobileNavProps) {
  const { t } = useAppLanguage();
  const { pathname } = useLocation();
  const [branding, setBranding] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAgency, setIsAgency] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(readAdvancedOpenInitial);

  useEffect(() => {
    fetch(`${getApiBase()}/global-config/public`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          setBranding({
            logo_url: data.logo_url ? `${getApiBase()}${data.logo_url}` : null,
            site_name: data.site_name || 'Totemium',
          });
        }
      })
      .catch(() => {});

    api
      .get('/fapi/users/me')
      .then((res) => {
        setIsAdmin(!!res.data?.is_superuser);
        if (res.data?.id) {
          api
            .get('/billing/subscription')
            .then((subRes) => {
              const isAgencyPlan = subRes.data?.plan?.max_workspaces > 1;
              setIsAgency(isAgencyPlan);
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (ADVANCED_PATH_RE.test(pathname)) {
      setAdvancedOpen(true);
    }
  }, [pathname]);

  const toggleAdvanced = useCallback(() => {
    setAdvancedOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('sidebar_advanced_open', String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const overviewItems: NavItem[] = [
    { name: t.sidebar.dashboard, href: '/', icon: LayoutDashboard },
    ...(isAgency
      ? [{ name: t.sidebar.agencyDashboard, href: '/agency/dashboard', icon: Building2 }]
      : []),
  ];

  const knowledgesItems: NavItem[] = [{ name: t.sidebar.knowledges, href: '/knowledges', icon: BookOpen }];

  const agentsItems: NavItem[] = [
    { name: t.sidebar.agents, href: '/agents', icon: Users },
    { name: t.sidebar.messenger, href: '/messenger', icon: MessagesSquare },
  ];

  const advancedItems: NavItem[] = [
    { name: t.sidebar.memory, href: '/memory', icon: Brain, badge: t.sidebar.badgePreview, badgeTone: 'preview' },
    {
      name: t.sidebar.endUsers,
      href: '/end-users',
      icon: UserCog,
      badge: t.sidebar.badgePreview,
      badgeTone: 'preview',
    },
    { name: t.sidebar.workflows, href: '/workflows', icon: Zap, badge: t.sidebar.badgeBeta, badgeTone: 'beta' },
    { name: t.sidebar.email, href: '/email', icon: Mail, badge: t.sidebar.badgePreview, badgeTone: 'preview' },
  ];

  const secondaryNavigation = [
    { name: t.sidebar.admin, href: '/admin', icon: Shield, adminOnly: true },
    ...(isAgency ? [{ name: t.sidebar.sharedApiKeys, href: '/agency/byok', icon: Key }] : []),
    { name: t.sidebar.configuration, href: '/config', icon: Settings },
    { name: t.sidebar.myAccount, href: '/account', icon: UserCircle },
  ];

  const renderNavLink = (item: NavItem, subtle?: boolean) => (
    <li key={`${item.href}-${item.name}`}>
      <NavLink
        to={item.href}
        onClick={onClose}
        className={({ isActive }) =>
          cn(
            'group flex gap-x-3 rounded-lg p-3 font-medium leading-6 transition-all duration-200',
            subtle ? 'text-xs text-zinc-500 py-2' : 'text-sm',
            isActive
              ? 'bg-emerald-500/10 text-emerald-400 shadow-[inset_3px_0_0_0_#10b981]'
              : cn(
                  'text-zinc-400 hover:bg-white/5 hover:text-white',
                  subtle && 'opacity-85'
                )
          )
        }
      >
        <item.icon className={cn('h-5 w-5 shrink-0', subtle && 'h-4 w-4')} aria-hidden="true" />
        <span className="flex-1 truncate">{item.name}</span>
        {item.badge && (
          <span
            className={cn(
              'ml-auto shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
              item.badgeTone === 'beta'
                ? 'bg-amber-500/15 text-amber-400/90'
                : 'bg-zinc-500/20 text-zinc-400'
            )}
          >
            {item.badge}
          </span>
        )}
      </NavLink>
    </li>
  );

  const renderSection = (title: string, items: NavItem[]) => (
    <div>
      <div className="text-[10px] font-semibold leading-6 text-zinc-500 px-2 mb-1.5 uppercase tracking-wider">
        {title}
      </div>
      <ul className="space-y-0.5">{items.map((i) => renderNavLink(i))}</ul>
    </div>
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] flex flex-col bg-zinc-950 border-r border-white/5 shadow-2xl animate-slide-in-left">
        <div className="flex h-16 shrink-0 items-center justify-between px-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-8 w-8 object-contain" />
            ) : (
              <div className="h-8 w-8 rounded bg-emerald-500 flex items-center justify-center">
                <LayoutDashboard className="h-5 w-5 text-white" />
              </div>
            )}
            <span className="font-bold text-lg text-white tracking-tight">
              {branding?.site_name || 'Totemium'}
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-zinc-400 hover:bg-white/5 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex flex-1 flex-col px-3 py-4 overflow-y-auto gap-y-5">
          {renderSection(t.sidebar.sectionOverview, overviewItems)}
          {renderSection(t.sidebar.sectionKnowledges, knowledgesItems)}
          {renderSection(t.sidebar.sectionAgentsDiscussions, agentsItems)}

          <div className="rounded-lg border border-white/5 bg-white/[0.02] p-1.5">
            <button
              type="button"
              onClick={toggleAdvanced}
              className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500 hover:bg-white/5 hover:text-zinc-400 transition-colors"
              aria-expanded={advancedOpen}
            >
              <span className="flex-1">{t.sidebar.sectionAdvanced}</span>
              <ChevronDown
                className={cn('h-3.5 w-3.5 shrink-0 text-zinc-500 transition-transform', advancedOpen && 'rotate-180')}
                aria-hidden
              />
            </button>
            {advancedOpen && (
              <ul className="space-y-0.5 border-t border-white/5 pt-1.5 mt-1">
                {advancedItems.map((i) => renderNavLink(i, true))}
              </ul>
            )}
          </div>

          <div className="mt-auto pt-2">
            <div className="text-[10px] font-semibold leading-6 text-zinc-500 px-2 mb-2 uppercase tracking-wider">
              {t.sidebar.system}
            </div>
            <ul className="space-y-1">
              {secondaryNavigation.map((item) => {
                if (item.adminOnly && !isAdmin) return null;
                return (
                  <li key={item.name}>
                    <NavLink
                      to={item.href}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          'group flex gap-x-3 rounded-lg p-3 text-sm font-medium leading-6 transition-all duration-200',
                          isActive
                            ? item.adminOnly
                              ? 'bg-violet-500/10 text-violet-400 shadow-[inset_3px_0_0_0_#8b5cf6]'
                              : 'bg-emerald-500/10 text-emerald-400'
                            : item.adminOnly
                              ? 'text-zinc-400 hover:bg-violet-500/10 hover:text-violet-400'
                              : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                        )
                      }
                    >
                      <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                      {item.name}
                    </NavLink>
                  </li>
                );
              })}
              <li>
                <button
                  className="w-full group flex gap-x-3 rounded-lg p-3 text-sm font-medium leading-6 text-zinc-400 hover:bg-red-500/10 hover:text-red-400 transition-all"
                  onClick={() => {
                    onLogout?.();
                    onClose();
                  }}
                >
                  <LogOut className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {t.sidebar.logout}
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </div>
  );
}

export function MobileBottomNav() {
  const { t } = useAppLanguage();

  /* Ordre aligné sur les sections du menu : Vue d’ensemble → Connaissances → Agents → Messenger (marque) → Configuration */
  const items = [
    { name: t.sidebar.dashboard, href: '/', icon: LayoutDashboard },
    { name: t.sidebar.knowledges, href: '/knowledges', icon: BookOpen },
    { name: t.sidebar.agents, href: '/agents', icon: Users },
    { name: t.sidebar.messenger, href: '/messenger', icon: MessagesSquare },
    { name: t.sidebar.configuration, href: '/config', icon: Settings },
  ];

  return (
    <nav className="fixed bottom-0 inset-x-0 z-40 lg:hidden border-t border-white/5 bg-zinc-950/95 backdrop-blur-xl">
      <div className="flex items-center justify-around h-16 px-1">
        {items.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.href === '/'}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center gap-0.5 flex-1 py-2 px-1 rounded-xl transition-all duration-200',
                isActive ? 'text-emerald-400' : 'text-zinc-500 hover:text-zinc-300'
              )
            }
          >
            {({ isActive }) => (
              <>
                <div
                  className={cn(
                    'flex items-center justify-center rounded-xl p-1.5 transition-all duration-200',
                    isActive ? 'bg-emerald-500/15' : ''
                  )}
                >
                  <item.icon className="h-5 w-5" />
                </div>
                <span className="text-[10px] font-medium leading-none truncate max-w-[56px] text-center">
                  {item.name}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
